import React, { useContext, useState, useEffect, useRef } from 'react';
import './Header.css';
import logo from '../../assets/images/vairo-logo.png';
import {ReactComponent as DownIcon} from '../../assets/icons/down-icon.svg';
import {ReactComponent as UnfoldIcon} from '../../assets/icons/unfold-icon.svg';
import {ReactComponent as WorkspaceIcon} from '../../assets/icons/team-name-icon.svg';
import {ReactComponent as UsageIcon} from '../../assets/icons/usage-icon.svg';
import {ReactComponent as SettingsIcon} from '../../assets/icons/settings-icon.svg';
import {ReactComponent as InfoIcon} from '../../assets/icons/info-icon.svg';
import {ReactComponent as SandboxIcon} from '../../assets/icons/info-icon-noFill.svg';
import {ReactComponent as RightIcon} from '../../assets/icons/arrow-right-icon.svg';
import {ReactComponent as DownloadIcon} from '../../assets/icons/download-icon.svg';
import { UserContext, WorkspaceContext, MenuContext, ActiveMenuIndexContext, MenuWidthContext, OrgContext, ForecastContext } from '../../objects/Context';
import { downloadExcelFromCashJson, generateExcelBlobFromCashJson } from '../../services/excelModel/excelCFModelServiceBrowser';
import CircleInitials from '../CircleInitials/CircleInitials'
import Profile from './Profile';
import Settings from './Settings';
import Info from './Info';
import Usage from './Usage';
import FeedbackOverlay from './FeedbackOverlay';
import axios from 'axios';
import Popup from '../../components/Popup/Popup';
import LoadingScreen from '../LoadingScreen/LoadingScreen';
import UsagePopup from '../ErrorHandler/UsagePopup';
import Alert from '../Alerts/Alert';
import { useNavigate } from 'react-router-dom';

const Header = () => {
    const { user, setUser } = useContext(UserContext);
    const { workspace, setWorkspace } = useContext(WorkspaceContext);
    const { org, setOrg } = useContext(OrgContext);
    const { isMenuOpen, setIsMenuOpen } = useContext(MenuContext);
    const { activeMenuIndex, activeSubMenuText, setActiveSubMenuText } = useContext(ActiveMenuIndexContext);
    const { menuWidth, setMenuWidth } = useContext(MenuWidthContext);
    const { currentForecast, activeScenario } = useContext(ForecastContext);
    const [isSandboxHovered, setIsSandboxHovered] = useState(false);
    const [workspacesOpen, setWorkspacesOpen] = useState(true);
    const [sandboxOpen, setSandboxOpen] = useState(false);
    // sandbox loading
    const [masterLoading, setMasterLoading] = useState(false);
    // Replace individual popup states with a single activePopup state
    const [activePopup, setActivePopup] = useState(null); // 'profile', 'settings', 'info', 'usage', 'workspaces', 'download'
    const [showAlert, setShowAlert] = useState(false);
    const [showAdminAlert, setShowAdminAlert] = useState(false);
    const [isExporting, setIsExporting] = useState(false);
    // Count of pending org invites addressed to this user (to other orgs)
    const [pendingInviteCount, setPendingInviteCount] = useState(0);

    const [usageData, setUsageData] = useState([]);
    // workspace dropdown
    const [loading, setLoading] = useState(false);
    const [loadingWorkspaces, setLoadingWorkspaces] = useState(false);
    const [showNewWorkspacePopup, setShowNewWorkspacePopup] = useState(false);
    const [workspaces, setWorkspaces] = useState([]);
    const [wsSearch, setWsSearch] = useState('');
    const inputRef = useRef(null);
    const navigate = useNavigate();
    
    // Refs for popup containers
    const profileRef = useRef(null);
    const settingsRef = useRef(null);
    const infoRef = useRef(null);
    const usageRef = useRef(null);
    const workspacesRef = useRef(null);
    const feedbackRef = useRef(null);
    const downloadRef = useRef(null);
    
    // useEffect to handle clicks outside popups
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (!activePopup) return;
            
            const clickedOnButton = 
                (activePopup === 'profile' && event.target.closest('.profile-button')) ||
                (activePopup === 'info' && event.target.closest('.header-icon-button.info')) ||
                (activePopup === 'usage' && event.target.closest('.header-icon-button.usage')) ||
                (activePopup === 'workspaces' && event.target.closest('.ws')) ||
                (activePopup === 'feedback' && event.target.closest('.header-icon-button.feedback')) ||
                (activePopup === 'download' && event.target.closest('.header-icon-button.download'));
                
            if (clickedOnButton) return;
            
            const currentRef = 
                activePopup === 'profile' ? profileRef.current :
                activePopup === 'info' ? infoRef.current :
                activePopup === 'usage' ? usageRef.current :
                activePopup === 'workspaces' ? workspacesRef.current :
                activePopup === 'feedback' ? feedbackRef.current :
                activePopup === 'download' ? downloadRef.current : null;
                
            if (currentRef && !currentRef.contains(event.target)) {
                setActivePopup(null);
                setWsSearch('');
            }
        };
        
        // Add event listener
        document.addEventListener('mousedown', handleClickOutside);
        
        // Cleanup
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [activePopup]);


    /////////////////// useEffects ///////////////////
    useEffect(() => {
        if (activePopup === 'workspaces' && !loadingWorkspaces && workspaces.length === 0) {
            getWorkspaces();
        }
    }, [activePopup]);

    useEffect(() => {
        fetchPendingInvites();
    }, [user?.email]);


    /////////////////// database functions ///////////////////
    const fetchPendingInvites = async () => {
        if (!user?.email) return;
        try {
            const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/users/get-org-invites/${user.email}`, { withCredentials: true });
            setPendingInviteCount(response.data?.invites?.length || 0);
        } catch (error) {
            // 204 (no invites) lands here in some setups; treat any failure as zero
            setPendingInviteCount(0);
        }
    };

    const handleLogout = async () => {
        try {
            await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/auth/logout`, {}, { withCredentials: true });
            setUser(null);
            setOrg(null);
            setWorkspace(null);
        } catch (error) {
            console.error('Logout failed:', error);
        }
    };

    const handleSwitchOrg = () => {
        setActivePopup(null);
        setUser((prev) => ({ ...prev, orgId: null }));
        setOrg(null);
        setWorkspace(null);
    };

    const getWorkspaces = async () => {
        setLoadingWorkspaces(true);
        try {
            const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/users/get-workspaces/${user.id}/${user.orgId}`, { withCredentials: true });
            setWorkspaces(response.data);
        } catch (error) {
            console.error('Get workspaces failed:', error);
        } finally {
            setLoadingWorkspaces(false);
        }
    };

    const switchWorkspace = async ({workspaceId}) => {
        try {
            const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/users/switch-workspace/${user.id}/${workspaceId}`, { withCredentials: true });
            window.location.href = '/';
        } catch (error) {
            console.error('Switch workspace failed:', error);
        }
    };

    const createWorkspace = async ({name}) => {
        if (name === '') {
            return;
        }
        // if (workspaces.length >= 5) {
        //     setActivePopup('usageLimit');
        //     return;
        // }
        setLoading(true);
        try {
            // Create the workspace
            const workspaceResponse = await axios.post(
                `${process.env.REACT_APP_API_BASE_URL}/api/workspaces/create-workspace`,
                {
                name: name,
                userId: user.id,
                orgId: user.orgId,
                accessLevel: 'team-wide',
                },
                { withCredentials: true }
            );
            switchWorkspace({workspaceId: workspaceResponse.data.id});
        } catch (err) {
            console.error('Error setting up workspace:', err);
            setShowAdminAlert(true);
        } finally {
            setLoading(false);
        }
    };

    const createSandboxWorkspace = async () => {
        if (user?.orgRole !== 'admin') {
            setShowAdminAlert(true);
            return;
        }

        setMasterLoading(true);
        try {
            const response = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/workspaces/create-sandbox`, {
                userId: user.id,
                orgId: user.orgId
            }, { withCredentials: true });
            switchWorkspace({workspaceId: response.data.id});
        } catch (err) {
            console.error('Error creating sandbox workspace:', err);
            if (err?.response?.status === 403) {
                setShowAdminAlert(true);
            } else {
                setShowAlert(true);
            }
        } finally {
            setMasterLoading(false);
        }
    }

    /////////////////// handlers ///////////////////
    const handleProfileClick = () => {
        setActivePopup(activePopup === 'profile' ? null : 'profile');
    };

    const handleSettingsClick = () => {
        navigate('/settings');
    };

    const handleInfoClick = () => {
        setActivePopup(activePopup === 'info' ? null : 'info');
    };

    const handleUsageClick = () => {
        setActivePopup(activePopup === 'usage' ? null : 'usage');
    };

    const handleMenuClick = () => {
        setIsMenuOpen(!isMenuOpen);
    };

    const getPathTitle = () => {
        let menuTitle = '';
        if (activeMenuIndex === 0) {
            menuTitle = "AI Forecast";
        // } else if (activeMenuIndex === 1) {
        //     menuTitle = "Dashboards";
        } else if (activeMenuIndex === 1) {
            menuTitle = "Reports";
        } else if (activeMenuIndex === 2) {
            menuTitle = "Options";
        } else if (activeMenuIndex === 3) {
            menuTitle = "Data Sources";
        } else if (activeMenuIndex === 4) {
            menuTitle = "Settings";
        } else if (activeMenuIndex === 11) {
            menuTitle = "Manage Subscription";
        } else if (activeMenuIndex === 12) {
            menuTitle = "Settings";
        }

        // Check if we're on a report page
        const isReportPage = window.location.pathname.startsWith('/reports/');
        if (isReportPage) {
            return "Reports";
        }

        return menuTitle;
    }

    const getSubText = () => {
        const isReportPage = window.location.pathname.startsWith('/reports/');
        if (isReportPage) {
            return "Report";
        }
        return activeSubMenuText;
    }

    const handlePathClick = ({ num }) => {
        if (num === 1) {
            setActivePopup(activePopup === 'workspaces' ? null : 'workspaces');
        } else if (num === 2) {
            if (activeSubMenuText) {
                window.history.back();
            } else if (window.location.pathname.startsWith('/reports/')) {
                navigate('/reports');
            }
        } else if (num === 3 && activeSubMenuText) {
        }
    };

    const handleWorkspaceClick = ({ws}) => {
        switchWorkspace({workspaceId: ws.id});
    };

    // Add handler for Feedback button
    const handleFeedbackClick = () => {
        setActivePopup(activePopup === 'feedback' ? null : 'feedback');
    };

    /////////////////// download / export ///////////////////
    const handleDownloadClick = () => {
        if (isExporting) return;
        setActivePopup(activePopup === 'download' ? null : 'download');
    };

    const fetchCashJsonForExport = async () => {
        const forecastId = currentForecast?.id;
        if (!forecastId) {
            throw new Error('No forecast available to export.');
        }
        const url = activeScenario
            ? `${process.env.REACT_APP_API_BASE_URL}/api/forecasts/scenarios/${forecastId}/compile/${encodeURIComponent(activeScenario)}`
            : `${process.env.REACT_APP_API_BASE_URL}/api/forecasts/compile/${forecastId}/${user?.id}/${workspace?.id}`;

        const response = await axios.get(url, { withCredentials: true });
        if (!response.data?.success || !response.data?.cashJson) {
            throw new Error('Failed to fetch cash JSON model');
        }
        return response.data.cashJson;
    };

    const buildExportFilename = () => {
        const wsName = (workspace?.name || workspace?.title || 'forecast').replace(/\s+/g, '_');
        return `${wsName}_${new Date().toISOString().slice(0,10)}`;
    };

    const buildExportMetadata = async () => {
        const clientPortal = org?.clientPortal || {};
        const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
        const logoDownloadUrl = (clientPortal.logoUrl && org?.id && apiBaseUrl)
            ? `${apiBaseUrl}/api/orgs/logo-file/${org.id}`
            : null;
        return {
            workspaceName: workspace?.name || workspace?.title || '',
            forecastName: currentForecast?.name || currentForecast?.title || 'Cash Forecast',
            branding: { ...clientPortal, logoDownloadUrl },
        };
    };

    const exportForecastAsPDF = async () => {
        if (!currentForecast?.id) {
            alert('No forecast loaded to export. Open a forecast first.');
            return;
        }
        try {
            setIsExporting(true);
            const cashJson = await fetchCashJsonForExport();
            const filename = buildExportFilename();
            const workbookBlob = await generateExcelBlobFromCashJson(cashJson, await buildExportMetadata());

            const formData = new FormData();
            formData.append('file', workbookBlob, `${filename}.xlsx`);

            const response = await axios.post(
                `${process.env.REACT_APP_API_BASE_URL}/api/forecasts/export-pdf`,
                formData,
                { withCredentials: true, responseType: 'blob' }
            );

            const pdfBlob = new Blob([response.data], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(pdfBlob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `${filename}.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.error('Error exporting PDF:', err);
            alert('Failed to export PDF. Please try again.');
        } finally {
            setIsExporting(false);
        }
    };

    const exportForecastAsExcel = async () => {
        if (!currentForecast?.id) {
            alert('No forecast loaded to export. Open a forecast first.');
            return;
        }
        try {
            setIsExporting(true);
            const cashJson = await fetchCashJsonForExport();
            const filename = buildExportFilename();
            await downloadExcelFromCashJson(cashJson, filename, await buildExportMetadata());
        } catch (err) {
            console.error('Excel export failed:', err);
            alert('Failed to export Excel. Please try again.');
        } finally {
            setIsExporting(false);
        }
    };

    /////////////////// render ///////////////////
    const renderWorkspaces = () => {
        const sortedRegular = workspaces
            .filter(ws => !ws.isSandbox)
            .sort((a, b) => a.name.localeCompare(b.name));
        const filteredRegular = sortedRegular.filter(ws =>
            ws.name.toLowerCase().includes(wsSearch.toLowerCase())
        );
        const sortedSandbox = workspaces
            .filter(ws => ws.isSandbox)
            .sort((a, b) => a.name.localeCompare(b.name));

        return (
        <div className="more-selection-menu ws-list">
            {loadingWorkspaces && renderLoadingWorkspaces()}
            <div className="ws-search-bar">
                <input
                    type="text"
                    placeholder="Search workspaces..."
                    value={wsSearch}
                    onChange={e => setWsSearch(e.target.value)}
                    onClick={e => e.stopPropagation()}
                />
            </div>
            <div className="ws-section-header" onClick={() => setWorkspacesOpen(o => !o)}>
                <span className="title">Your Workspaces {sortedRegular.length > 0 && `(${sortedRegular.length})`}</span>
                <DownIcon className={`ws-section-chevron${workspacesOpen ? ' open' : ''}`} />
            </div>
            {workspacesOpen && (
                <div className="workspace-list">
                    {filteredRegular.length > 0
                        ? filteredRegular.map((ws) => (
                            <div key={ws.id} className='workspace' onClick={() => handleWorkspaceClick({ws})}>
                                <WorkspaceIcon className='icon' />
                                <span className='name'>{ws.name}</span>
                                <RightIcon className='icon' />
                            </div>
                        ))
                        : <span className="ws-no-results">No workspaces found</span>
                    }
                </div>
            )}
            {sortedSandbox.length > 0 && (
                <>
                    <div className="ws-section-header" onClick={() => setSandboxOpen(o => !o)}>
                        <span className="title">Your Sandbox Accounts {sortedSandbox.length > 0 && `(${sortedSandbox.length})`}</span>
                        <DownIcon className={`ws-section-chevron${sandboxOpen ? ' open' : ''}`} />
                    </div>
                    {sandboxOpen && (
                        <div className="workspace-list">
                            {sortedSandbox.map((ws) => (
                                <div key={ws.id} className='workspace' onClick={() => handleWorkspaceClick({ws})}>
                                    <WorkspaceIcon className='icon' />
                                    <span className='name'>{ws.name}</span>
                                    <RightIcon className='icon' />
                                </div>
                            ))}
                        </div>
                    )}
                </>
            )}
           
            <div className="actions">
                <div className="action confirm" onClick={() => {
                    setShowNewWorkspacePopup(true);
                }}>
                    <span>New Workspace</span>
                </div>
                <div className="divider">- or -</div>
                <div
                    className="action sandbox"
                    onClick={() => { createSandboxWorkspace(); }}
                    onMouseEnter={() => setIsSandboxHovered(true)}
                    onMouseLeave={() => setIsSandboxHovered(false)}
                    style={{ position: 'relative' }}
                >
                    {isSandboxHovered && (
                        <div className="sandbox-tooltip">
                            Our <strong>sandbox workspace</strong> is a temporary environment where you can try out Vairo using a demo QuickBooks account that's already set up.
                        </div>
                    )}
                    <span>Try Sandbox Workspace</span>
                    <SandboxIcon className='icon' />
                </div>
            </div>
        </div>
        );
    };

    const renderLoadingWorkspaces = () => (
        <div className='loading-workspaces'>
            <span>Loading workspaces...</span>
        </div>
    );

    const renderNewWorkspacePopup = () => (
        <Popup
            content={
                <div className="add-item-wrapper">
                    <h2>New Workspace</h2>
                    <div className="add-item-inputs">
                        <input type="text" placeholder="Workspace Name" ref={inputRef} />
                    </div>

                    <div className="add-item-actions">
                        <button className="add-item-button cancel" onClick={() => setShowNewWorkspacePopup(false)}>Cancel</button>
                        <button className="add-item-button create" onClick={() => createWorkspace({name: inputRef.current.value})}>Create Workspace</button>
                    </div>
                    {loading && <LoadingScreen />}
                </div> 
            }
            onClose={() => setShowNewWorkspacePopup(false)}
        />
    );

    const renderMasterLoading = () => (
        <Popup
            content={
                <div className="master-loading">
                    <LoadingScreen message="Setting up workspace..." />
                </div> 
            }
            onClose={() => {}}
        />
    );

    const renderAlert = () => (
        <Alert
            message="There was an Issue Creating Your Sandbox."
            submessage="Vairo only supports one sandbox workspace per organization. Please reach out to us if you'd like to increase this limit!"
            success={false}
            onClose={() => setShowAlert(false)}
        />
    );

    const renderAdminAlert = () => (
        <Alert
            message="You are not authorized to create a workspace."
            submessage="Please contact your administrator to create a workspace."
            success={false}
            onClose={() => setShowAdminAlert(false)}
        />
    );


    return (
        <header className='product-header'>
            <div className="container">
                <div className="left-container">
                    <div className="menu-button" onClick={handleMenuClick}>
                        <div className="line one"></div>
                        <div className="line two"></div>
                    </div>
                    <div className="logo">
                        <img src={logo} alt="Filo Logo" />
                    </div>
                </div>
                <div
                    className="path-container"
                    style={{ left: menuWidth }}
                >
                    <div className='ws' onClick={() => handlePathClick({num: 1})}>
                        <span className='path-text' >{workspace.name}</span>
                        {workspace.name && <UnfoldIcon className='path-icon down' />}
                    </div>
                    {activePopup === 'workspaces' && (
                        <div ref={workspacesRef}>
                            {renderWorkspaces()}
                        </div>
                    )}
                    <span className={`path-text ${getSubText() ? '' : 'subtext'}`} onClick={() => handlePathClick({num: 2})}>{getPathTitle()}</span>
                    {(getSubText() || window.location.pathname.startsWith('/reports/')) && <RightIcon className='path-icon' />}
                    {getSubText() && <span className='path-text subtext' onClick={() => handlePathClick({num: 3})}>{getSubText()}</span>}
                </div>
                <div className='button-container'>
                    {/* <div className='alerts'>
                        <AlertIcon className='header-icon-button' />
                        <div className='alerts-count'><span>3</span></div>
                    </div> */}
                    <div className='button'>
                        <div className='button-text' onClick={handleFeedbackClick}>
                            Give Feedback
                        </div>
                        {activePopup === 'feedback' && (
                            <Popup
                                content={ <FeedbackOverlay onClose={() => setActivePopup(null)} />}
                                onClose={() => setActivePopup(null)}
                            />
                        )}
                    </div>
                    {/* <div className='button'>
                        <UsageIcon className='header-icon-button usage' onClick={handleUsageClick} />
                        {activePopup === 'usage' && (<div ref={usageRef}><Usage usageData={usageData} setUsageData={setUsageData} closeUsage={() => setActivePopup(null)} /></div>)}
                    </div> */}
                    {/* <div className='button'>
                        <SettingsIcon className='header-icon-button settings' onClick={handleSettingsClick} />
                    </div> */}
                    <div className='button'>
                        <InfoIcon className='header-icon-button info' onClick={handleInfoClick} />
                        {activePopup === 'info' && (<div ref={infoRef}><Info /></div>)}
                    </div>
                    {/* <div className='button'>
                        {isExporting ? (
                            <div className='header-download-spinner'>
                                <div className='spinner'></div>
                            </div>
                        ) : (
                            <DownloadIcon
                                className='header-icon-button download'
                                onClick={handleDownloadClick}
                                title={currentForecast?.id ? 'Download forecast' : 'Open a forecast to download'}
                            />
                        )}
                        {activePopup === 'download' && (
                            <div ref={downloadRef} className='download-menu'>
                                {currentForecast?.id ? (
                                    <>
                                        <div
                                            className='download-option'
                                            onClick={() => {
                                                setActivePopup(null);
                                                exportForecastAsPDF();
                                            }}
                                        >
                                            <span>Download to PDF</span>
                                        </div>
                                        <div
                                            className='download-option'
                                            onClick={() => {
                                                setActivePopup(null);
                                                exportForecastAsExcel();
                                            }}
                                        >
                                            <span>Download to Excel</span>
                                        </div>
                                    </>
                                ) : (
                                    <div className='download-option disabled'>
                                        <span>No forecast loaded</span>
                                    </div>
                                )}
                            </div>
                        )}
                    </div> */}
                    <div className='header-divider'>|</div>
                    <button className="profile-button" onClick={handleProfileClick}>
                        <CircleInitials text={user.name} classN='header-initials' />
                        <h3>{user.name}</h3>
                        <DownIcon className='header-button'/>
                    </button>
                </div>
            </div>
            {activePopup === 'profile' && (
                <div ref={profileRef}>
                    <Profile user={user} workspace={workspace} org={org} setShowPopup={() => setActivePopup(null)} handleLogout={handleLogout} handleSwitchOrg={handleSwitchOrg} pendingInviteCount={pendingInviteCount} />
                </div>
            )}
           
            {showNewWorkspacePopup && renderNewWorkspacePopup()}
            {activePopup === 'usageLimit' && (
                <UsagePopup
                    message={"Workspace Limit Reached"}
                    submessage={"We currently only support 5 workspaces per organization. Please reach out to us if you'd like to increase this limit!"}
                    onClose={() => setActivePopup(null)}
                />
            )}
            {masterLoading && renderMasterLoading()}
            {showAlert && renderAlert()}
            {showAdminAlert && renderAdminAlert()}
        </header>
    );
};

export default Header;